// Sprint T-21 · Fase 1e · NotificationInboxDropdown (In-App básico)
// Sprint T-22 · refactor · añade botón "Ver todas" → abre NotificationCenter
//
// Icono/botón notificaciones en topbar → badge unread-count → dropdown con
// las últimas notificaciones (lista, estado, deep_link, mark_read, mark_all).
//
// Utiliza los endpoints creados en F1d + T-22:
//   - GET  /api/notifications/inbox
//   - GET  /api/notifications/unread-count
//   - POST /api/notifications/{id}/read
//   - POST /api/notifications/read-all
//
// Auth: dual (user + customer) vía cookie `ag_user_token` / `ag_customer_token`.
//
// El dropdown mantiene el rol de "quick preview" (5 últimas) · el resto de
// experiencia (filtros, paginación, preferencias) vive en NotificationCenter.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { BellRinging, Check, CheckCircle, CircleNotch, X } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { NotificationCenter } from "./NotificationCenter";

const POLL_INTERVAL_MS = 30_000;

function formatRelative(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const s = Math.floor(diff / 1000);
    if (s < 60) return "ahora";
    const m = Math.floor(s / 60);
    if (m < 60) return `hace ${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `hace ${h} h`;
    const d = Math.floor(h / 24);
    if (d < 7) return `hace ${d} d`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return "";
  }
}

export const NotificationInboxDropdown = ({ className = "" }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [centerOpen, setCenterOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);
  const ref = useRef(null);

  // Fetch unread-count (con poll ligero)
  const fetchUnread = useCallback(async () => {
    try {
      const r = await api.get("/notifications/unread-count");
      if (typeof r?.data?.unread_count === "number") {
        setUnreadCount(r.data.unread_count);
      }
    } catch (e) {
      // 401 (no logueado) = silencioso. Otros errores ignorados.
    }
  }, []);

  // Fetch inbox items · Sprint T-22: quick preview limitado a 5 últimas.
  const fetchInbox = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/notifications/inbox?limit=5");
      setItems(r?.data?.items || []);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        setError(null);           // silencioso · usuario no autenticado
        setItems([]);
      } else {
        setError("No se pudo cargar la bandeja");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    const iv = setInterval(fetchUnread, POLL_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [fetchUnread]);

  useEffect(() => {
    if (open) fetchInbox();
  }, [open, fetchInbox]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleItemClick = async (n) => {
    // Optimistic: marcar leído
    if (!n.read) {
      setItems((prev) =>
        prev.map((it) =>
          it.id === n.id ? { ...it, read: true, read_at: new Date().toISOString() } : it,
        ),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      try {
        await api.post(`/notifications/${n.id}/read`);
      } catch (e) {
        // Silencioso · idempotente
      }
    }
    if (n.deep_link) {
      setOpen(false);
      // Deep links pueden ser rutas internas (/appointments/xyz) o URLs.
      if (/^https?:\/\//i.test(n.deep_link)) {
        window.location.href = n.deep_link;
      } else {
        navigate(n.deep_link);
      }
    }
  };

  const handleMarkAll = async () => {
    setMarkingAll(true);
    try {
      const r = await api.post("/notifications/read-all");
      const modified = r?.data?.modified_count || 0;
      setItems((prev) =>
        prev.map((it) => ({ ...it, read: true, read_at: new Date().toISOString() })),
      );
      setUnreadCount(0);
    } catch (e) {
      setError("No se pudo marcar todo como leído");
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <div ref={ref} className={`relative ${className}`} data-testid="notif-inbox-dropdown">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex items-center justify-center h-9 w-9 rounded-md border border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white hover:bg-[var(--ag-midnight)]/60 transition-colors"
        aria-label="Bandeja de notificaciones"
        data-testid="notif-inbox-toggle"
      >
        <BellRinging size={18} weight="regular" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--ag-amber,#f59e0b)] text-[var(--ag-midnight,#0f172a)] text-[10px] font-bold flex items-center justify-center leading-none"
            data-testid="notif-inbox-badge"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[360px] max-w-[92vw] max-h-[480px] rounded-xl border border-[var(--ag-border)] bg-[var(--ag-midnight)] shadow-2xl overflow-hidden z-50 flex flex-col"
          data-testid="notif-inbox-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--ag-border)]">
            <div className="text-sm font-semibold text-white">Notificaciones</div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAll}
                  disabled={markingAll}
                  className="text-xs text-[var(--ag-text-dim)] hover:text-white transition-colors disabled:opacity-40"
                  data-testid="notif-inbox-mark-all"
                >
                  {markingAll ? "…" : "Marcar todas"}
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[var(--ag-text-dim)] hover:text-white"
                aria-label="Cerrar"
                data-testid="notif-inbox-close"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading && (
              <div
                className="flex items-center justify-center py-8 text-[var(--ag-text-dim)]"
                data-testid="notif-inbox-loading"
              >
                <CircleNotch size={22} className="animate-spin" />
              </div>
            )}
            {!loading && error && (
              <div
                className="px-4 py-6 text-sm text-red-300"
                data-testid="notif-inbox-error"
              >
                {error}
              </div>
            )}
            {!loading && !error && items.length === 0 && (
              <div
                className="px-4 py-10 text-center text-sm text-[var(--ag-text-dim)]"
                data-testid="notif-inbox-empty"
              >
                Sin notificaciones nuevas
              </div>
            )}
            {!loading && !error && items.length > 0 && (
              <ul className="divide-y divide-[var(--ag-border)]/60">
                {items.map((n) => (
                  <li
                    key={n.id}
                    className={`px-4 py-3 hover:bg-[var(--ag-midnight)]/80 transition-colors ${
                      n.deep_link ? "cursor-pointer" : ""
                    } ${!n.read ? "bg-[var(--ag-midnight)]/60" : ""}`}
                    onClick={() => handleItemClick(n)}
                    data-testid={`notif-inbox-item-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && (
                        <span
                          className="mt-1.5 shrink-0 w-2 h-2 rounded-full bg-[var(--ag-amber,#f59e0b)]"
                          data-testid={`notif-inbox-item-dot-${n.id}`}
                          aria-label="No leída"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white font-medium truncate">
                          {n.title || "Notificación"}
                        </div>
                        {n.body && (
                          <div className="text-xs text-[var(--ag-text-dim)] mt-0.5 line-clamp-2">
                            {n.body}
                          </div>
                        )}
                        <div className="text-[11px] text-[var(--ag-text-dim)]/80 mt-1">
                          {formatRelative(n.created_at)}
                          {n.notification_kind && (
                            <span className="ml-2 opacity-60">· {n.notification_kind}</span>
                          )}
                        </div>
                      </div>
                      {n.read && (
                        <Check size={14} className="text-[var(--ag-text-dim)]/60 mt-1" />
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Sprint T-22 · Footer con acceso al Notification Center */}
          <div className="border-t border-[var(--ag-border)] px-4 py-2.5">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setCenterOpen(true);
              }}
              className="w-full text-center text-xs font-semibold text-[var(--ag-amber,#f59e0b)] hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ag-amber,#f59e0b)]/50 rounded py-1"
              data-testid="notif-inbox-open-center"
            >
              Ver todas las notificaciones
            </button>
          </div>
        </div>
      )}

      {/* Sprint T-22 · Notification Center completo (modal) */}
      <NotificationCenter
        open={centerOpen}
        onOpenChange={(next) => {
          setCenterOpen(next);
          if (!next) {
            // Al cerrar el modal, refrescar el badge y las 5 últimas del dropdown
            fetchUnread();
            fetchInbox();
          }
        }}
      />
    </div>
  );
};

export default NotificationInboxDropdown;
